import React, { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import Select from "../common/Select"
import Button from "../common/Button"
import { useLocation } from "react-router-dom"
import { validateEmail as validateEmailDNS } from "email-validator-dns-provider-rules"
import IntlTelInput, { IntlTelInputRef } from "intl-tel-input/react"
import "intl-tel-input/styles"

// Filter options based on spec requirements
const INDUSTRY_OPTIONS = [
  { value: "beauty-cosmetics", label: "Beauty & Cosmetics" },
  { value: "consumer-electronics", label: "Consumer Electronics" },
  { value: "food-beverage", label: "Food & Beverage" },
  { value: "other", label: "My industry isn't listed" },
]

const COUNTRY_OPTIONS = [
  { value: "uk", label: "United Kingdom" },
  { value: "germany", label: "Germany" },
  { value: "italy", label: "Italy" },
  { value: "united-states", label: "United States" },
  { value: "other", label: "My Country is not listed" },
]

const BUDGET_OPTIONS = [
  { value: "0-4999", label: "Up to €5,000" },
  { value: "5000-10000", label: "€5,000 - €10,000" },
  { value: "10000+", label: "€10,000 and above" },
]

// List of common personal email domains that we want to block
const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "mail.com",
  "protonmail.com",
  "zoho.com",
  "yandex.com",
  "gmx.com",
  "live.com",
  "me.com",
  "inbox.com",
  "googlemail.com",
]

interface FilterSectionProps {
  onSubmit: (filters: FilterValues) => void
  initialValues?: FilterValues
  isLoading?: boolean
  signupUrl?: string
  onFieldChange?: (field: keyof FilterValues, value: string) => void
}

export interface FilterValues {
  industry: string
  country: string
  budget: string
  businessEmail: string
  companyName: string
  phoneNumber?: string
}

interface EmailValidationState {
  isValidating: boolean
  isValid: boolean | null
  lastValidatedEmail: string
}

const FilterContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[6]};
  box-shadow: ${({ theme }) => theme.boxShadow.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  margin-top: ${({ theme }) => theme.spacing[2]};
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing[6]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.backgroundDark};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  text-align: left;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes["2xl"]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
    margin-bottom: ${({ theme }) => theme.spacing[6]};
  }
`

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
`

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  &:last-child {
    margin-bottom: 0;
  }
`

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: block;
  text-align: left;
`

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-family: ${({ theme }) => theme.fonts.primary};
  height: 54px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const InputContainer = styled.div`
  position: relative;
`

const ValidationSpinner = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #4361ee;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: translateY(-50%) rotate(0deg);
    }
    100% {
      transform: translateY(-50%) rotate(360deg);
    }
  }
`

const ValidationMessage = styled.p<{ type: "error" | "success" | "info" }>`
  font-size: 0.75rem !important;
  margin-top: ${({ theme }) => theme.spacing[1]};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ type, theme }) => {
    switch (type) {
      case "error":
        return theme.colors.error || "#EF4444"
      case "success":
        return theme.colors.success || "#10B981"
      case "info":
        return theme.colors.primary || "#4361EE"
      default:
        return theme.colors.text || "#1E293B"
    }
  }} !important;
  text-align: left !important;
  font-weight: 500 !important;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) =>
    theme.spacing[4]}; /* Reduced from larger margin */

  button {
    max-width: 300px;
  }
`

// Modal components for "not listed" options
const Modal = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(12, 21, 40, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 200;
  padding: ${({ theme }) => theme.spacing[4]};
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  transition: all 0.3s ease;
`

const ModalContent = styled.div`
  background-color: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  max-width: 600px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  text-align: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing[3]};
  right: ${({ theme }) => theme.spacing[3]};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textLight};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;

  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundAlt};
  }
`

const ModalHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`

const ModalBadge = styled.span`
  display: inline-block;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  text-transform: uppercase;
  letter-spacing: 1px;
`

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes["2xl"]};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`

const ModalText = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.text} !important;
  font-size: ${({ theme }) => theme.fontSizes.md};
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ModalList = styled.ul`
  text-align: left;
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  padding-left: ${({ theme }) => theme.spacing[4]};
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ModalListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  position: relative;
  padding-left: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.fontSizes.md};

  &:before {
    content: "•";
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
`

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
  }
`

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CTAButton = styled(Button)`
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: ${({ theme }) => theme.colors.primary}; /* #4361EE */
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.3);
    background: #3d5bdb; /* Same hover color as primary */
    color: white;
  }

  &:active {
    transform: translateY(0);
    background: #3751d1; /* Same active color as primary */
  }
`

// Outlined button styling for secondary actions like "Go Back"
const OutlinedButton = styled(Button)`
  border: 2px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  background-color: transparent;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[6]}`};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(67, 97, 238, 0.1);
    background-color: rgba(67, 97, 238, 0.05);
  }

  &:active {
    transform: translateY(0);
    background-color: rgba(67, 97, 238, 0.1);
  }
`

const FilterSection: React.FC<FilterSectionProps> = ({
  onSubmit,
  initialValues,
  isLoading = false,
  signupUrl,
  onFieldChange,
}) => {
  const [values, setValues] = useState<FilterValues>(
    initialValues || {
      industry: "",
      country: "",
      budget: "",
      businessEmail: "",
      companyName: "",
    }
  )

  const [errors, setErrors] = useState<Partial<FilterValues>>({})
  const [showNotListedModal, setShowNotListedModal] = useState(false)
  const [modalType, setModalType] = useState<"industry" | "country">("industry")
  const [freeDomains, setFreeDomains] = useState<Set<string>>(new Set())
  const [phoneNumber, setPhoneNumber] = useState("")
  const [phoneValidity] = useState<boolean>(true)
  const phoneInputRef = useRef<IntlTelInputRef>(null)

  // Anti-spam protection states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0)
  const [honeypot, setHoneypot] = useState("") // Hidden field for bot detection
  const formStartTime = useRef<number>(Date.now()) // Track when form was first rendered

  // Email validation state
  const [emailValidation, setEmailValidation] = useState<EmailValidationState>({
    isValidating: false,
    isValid: null,
    lastValidatedEmail: "",
  })
  const emailValidationTimeout = useRef<NodeJS.Timeout | null>(null)

  // Rate limiting constants
  const MIN_SUBMISSION_INTERVAL = 5000 // 5 seconds between submissions
  const MIN_FORM_INTERACTION_TIME = 1000 // Minimum 3 seconds to fill form

  // UTM
  const location = useLocation()
  const urlParams = new URLSearchParams(window.location.search)
  const utm = urlParams.get("utm_source") || urlParams.get("UTM_SOURCE") || ""
  const utmMedium =
    urlParams.get("utm_medium") || urlParams.get("UTM_MEDIUM") || ""
  const utmCampaign =
    urlParams.get("utm_campaign") || urlParams.get("UTM_CAMPAIGN") || ""
  const utmContent =
    urlParams.get("utm_content") || urlParams.get("UTM_CONTENT") || ""
  const utmTerm = urlParams.get("utm_term") || urlParams.get("UTM_TERM") || ""
  const referrer = document.referrer || ""

  // Load free domains CSV on component mount
  useEffect(() => {
    const loadFreeDomains = async () => {
      try {
        // For now, we'll use the existing PERSONAL_EMAIL_DOMAINS list
        // In the future, you could load from a CSV file like the landings project
        setFreeDomains(new Set(PERSONAL_EMAIL_DOMAINS))
      } catch (error) {
        console.error("Failed to load free domains:", error)
      }
    }

    loadFreeDomains()
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emailValidationTimeout.current) {
        clearTimeout(emailValidationTimeout.current)
      }
    }
  }, [])

  const handleChange = (field: keyof FilterValues, value: string) => {
    // Check if the user selected "not listed" option
    if ((field === "industry" || field === "country") && value === "other") {
      setModalType(field)
      setShowNotListedModal(true)
    }

    setValues((prev) => ({
      ...prev,
      [field]: value,
    }))

    // Clear error when field is updated
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }

    // Handle email validation with debounce
    if (field === "businessEmail") {
      // Clear previous timeout
      if (emailValidationTimeout.current) {
        clearTimeout(emailValidationTimeout.current)
      }

      // Reset validation state when user starts typing
      setEmailValidation({
        isValidating: false,
        isValid: null,
        lastValidatedEmail: "",
      })

      // Clear errors when user starts typing
      if (errors.businessEmail) {
        setErrors((prev) => ({
          ...prev,
          businessEmail: undefined,
        }))
      }

      // Debounce email validation
      emailValidationTimeout.current = setTimeout(() => {
        performDNSValidation(value)
      }, 2000)
    }

    // If onFieldChange is provided, call it to update parent component state
    if (onFieldChange) {
      onFieldChange(field, value)
    }
  }

  // Basic email validation (non-async)
  const validateEmailBasic = (email: string): string | null => {
    if (!email) {
      return "Email is required"
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address"
    }

    // Extract domain from email
    const domain = email.split("@")[1]?.toLowerCase()
    if (!domain) {
      return "Please enter a valid email address"
    }

    const provider = domain.split(".")[0]
    if (
      provider.includes("gmail") ||
      provider.includes("yahoo") ||
      provider.includes("aol") ||
      provider.includes("hotmail") ||
      provider.includes("outlook")
    ) {
      return "Please use your business email address. Free email providers (Gmail, Yahoo, etc.) are not allowed."
    }

    // Check if domain is in free domains list
    if (freeDomains.has(domain)) {
      return "Please use your business email address. Free email providers (Gmail, Yahoo, etc.) are not allowed."
    }

    return null
  }

  // DNS validation (async)
  const performDNSValidation = async (email: string): Promise<void> => {
    const trimmedEmail = email.trim().toLowerCase()

    // Skip validation if email is empty or already validated
    if (!trimmedEmail || emailValidation.lastValidatedEmail === trimmedEmail) {
      return
    }

    // First check basic validation
    const basicError = validateEmailBasic(trimmedEmail)
    if (basicError) {
      setErrors({ businessEmail: basicError })
      setEmailValidation({
        isValidating: false,
        isValid: false,
        lastValidatedEmail: trimmedEmail,
      })
      return
    }

    try {
      setEmailValidation({
        isValidating: true,
        isValid: null,
        lastValidatedEmail: trimmedEmail,
      })
      setErrors((prev) => ({ ...prev, businessEmail: undefined })) // Clear any previous errors

      // Use the DNS validation library with blocklist for common typos
      const blocklistDomains = [
        "gmial.com", // Gmail typo
        "hotnail.com", // Hotmail typo
        "yahooo.com", // Yahoo typo
        "outlok.com", // Outlook typo
        "gmai.com", // Gmail typo
        "yahoo.co", // Yahoo incomplete
        "hotmial.com", // Hotmail typo
        "10minutemail.com",
        "mailinator.com",
        "guerrillamail.com",
        "tempmail.org",
        "disposablemail.com",
      ]

      const result = await validateEmailDNS(trimmedEmail, { blocklistDomains })

      if (result.valid) {
        setEmailValidation({
          isValidating: false,
          isValid: true,
          lastValidatedEmail: trimmedEmail,
        })
        setErrors((prev) => ({ ...prev, businessEmail: undefined })) // Clear any errors
      } else {
        let errorMessage = "This email address is not valid."

        // Provide specific error messages based on the validation result
        if (result.reasonText) {
          if (result.reasonText.includes("DNS MX records")) {
            errorMessage =
              "This email domain doesn't exist or can't receive emails. Please use your business email address."
          } else if (result.reasonText.includes("blocklist")) {
            errorMessage =
              "This email domain is not allowed. Please use your business email address."
          } else if (result.reasonText.includes("provider")) {
            errorMessage =
              "This email format is not supported by the provider. Please use your business email address."
          } else if (result.reasonText.includes("typo")) {
            errorMessage =
              "This domain appears to be a typo. Please use your business email address."
          } else {
            errorMessage = result.reasonText
          }
        }

        setErrors({ businessEmail: errorMessage })
        setEmailValidation({
          isValidating: false,
          isValid: false,
          lastValidatedEmail: trimmedEmail,
        })
      }
    } catch (error) {
      console.error("DNS email validation error:", error)

      // Fallback to basic validation if DNS check fails
      const basicError = validateEmailBasic(trimmedEmail)
      if (basicError) {
        setErrors({ businessEmail: basicError })
        setEmailValidation({
          isValidating: false,
          isValid: false,
          lastValidatedEmail: trimmedEmail,
        })
      } else {
        // If basic validation passes but DNS fails, allow the email
        setEmailValidation({
          isValidating: false,
          isValid: true,
          lastValidatedEmail: trimmedEmail,
        })
        setErrors((prev) => ({ ...prev, businessEmail: undefined }))
      }
    }
  }

  // Enhanced anti-spam validation
  const validateAntiSpam = (): string | null => {
    const now = Date.now()

    // Check honeypot field (should be empty)
    if (honeypot) {
      console.warn("Bot detected: honeypot field filled")
      return "Suspicious activity detected"
    }

    // Check minimum form interaction time
    if (now - formStartTime.current < MIN_FORM_INTERACTION_TIME) {
      console.warn("Bot detected: form filled too quickly")
      return "Please take your time filling out the form"
    }

    // Check rate limiting
    if (now - lastSubmissionTime < MIN_SUBMISSION_INTERVAL) {
      console.warn("Rate limit exceeded: too frequent submissions")
      return `Please wait ${Math.ceil(
        (MIN_SUBMISSION_INTERVAL - (now - lastSubmissionTime)) / 1000
      )} seconds before submitting again`
    }

    return null
  }

  // Send form data to Zapier webhook
  const sendToZapierWebhook = async (formData: FilterValues) => {
    if (process.env.NODE_ENV !== "production") return

    const ZAPIER_WEBHOOK_URL =
      "https://hooks.zapier.com/hooks/catch/21165288/uomz1qk/"

    try {
      await fetch(ZAPIER_WEBHOOK_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({
          industry: formData.industry,
          country: formData.country,
          budget: formData.budget,
          businessEmail: formData.businessEmail,
          companyName: formData.companyName,
          phoneNumber,
          timestamp: new Date().toISOString(),
          source: "freemium-influencer-list",
          source_url: window.location.href,
          utmSource: utm,
          utmMedium: utmMedium,
          utmCampaign: utmCampaign,
          utmContent: utmContent,
          utmTerm: utmTerm,
          referrer: referrer,
        }),
      })

      // With no-cors mode, we can't check response.ok as the response is opaque
      // We'll assume success if no error was thrown
    } catch (error) {
      console.error("Error sending data to webhook:", error)
      // Don't block form submission if webhook fails
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent multiple simultaneous submissions
    if (isSubmitting) {
      return
    }

    setIsSubmitting(true)

    try {
      // Anti-spam validation
      const spamError = validateAntiSpam()
      if (spamError) {
        setErrors({ businessEmail: spamError })
        return
      }

      // Validate
      const newErrors: Partial<FilterValues> = {}
      let isValid = true
      let shouldShowModal = false
      let modalFieldType: "industry" | "country" = "industry" // Default

      // Check required fields
      if (!values.industry) {
        newErrors.industry = "Please select an industry"
        isValid = false
      } else if (values.industry === "other") {
        newErrors.industry = "Please select a standard industry option"
        isValid = false
        shouldShowModal = true
        modalFieldType = "industry"
      }

      if (!values.country) {
        newErrors.country = "Please select a country"
        isValid = false
      } else if (values.country === "other") {
        newErrors.country = "Please select a standard country option"
        isValid = false
        // Only override the modal type if industry isn't "other"
        if (!shouldShowModal) {
          shouldShowModal = true
          modalFieldType = "country"
        }
      }

      if (!values.budget) {
        newErrors.budget = "Please select a budget range"
        isValid = false
      }

      if (!values.companyName) {
        newErrors.companyName = "Company name is required."
        isValid = false
      }

      if (!phoneValidity) {
        newErrors.phoneNumber = "Phone number is required."
        isValid = false
      }

      // Check if email validation is still in progress
      if (emailValidation.isValidating) {
        newErrors.businessEmail =
          "Please wait while we verify your email address..."
        isValid = false
      }

      // Check if email has been validated and is valid
      if (!emailValidation.isValidating && emailValidation.isValid !== true) {
        // If email hasn't been validated yet, run validation now
        if (
          emailValidation.lastValidatedEmail !==
          values.businessEmail.trim().toLowerCase()
        ) {
          await performDNSValidation(values.businessEmail)
          newErrors.businessEmail =
            "Please wait while we verify your email address..."
          isValid = false
        } else if (emailValidation.isValid === false) {
          // If validation failed, show error
          newErrors.businessEmail =
            errors.businessEmail ||
            "Please enter a valid business email address."
          isValid = false
        }
      }

      // Set the errors first
      setErrors(newErrors)

      // Show the modal if needed
      if (shouldShowModal) {
        setModalType(modalFieldType)
        setShowNotListedModal(true)
        return
      }

      // Only submit if valid
      if (isValid) {
        // Update anti-spam tracking
        const now = Date.now()
        setLastSubmissionTime(now)

        // Send data to Zapier webhook (non-blocking)
        await sendToZapierWebhook(values)

        // Continue with normal form submission
        onSubmit(values)
      }
    } finally {
      // Re-enable form after a delay to prevent rapid resubmissions
      setTimeout(() => {
        setIsSubmitting(false)
      }, 2000)
    }
  }

  const closeModal = () => {
    setShowNotListedModal(false)
  }

  // Generate a custom sign-up URL for the modal that correctly reflects the "not listed" selection
  const getCustomSignupUrl = () => {
    // If we have a signupUrl from the parent, prefer using that as it will have the most up-to-date state
    if (signupUrl) {
      // Parse the existing URL to get the base and parameters
      const url = new URL(signupUrl)
      const params = url.searchParams

      // Update the industry or country parameter based on the modal type
      if (modalType === "industry") {
        params.set("source_industry", "not_listed")
      } else if (modalType === "country") {
        params.set("source_country", "not_listed")
      }

      // Return the updated URL
      return url.toString()
    }

    // Fallback to building the URL from scratch if no signupUrl is provided
    const baseUrl = "https://business.hypefy.ai/auth/signup"
    const params = new URLSearchParams()

    // Add parameters based on current form values
    if (values.industry) {
      if (modalType === "industry") {
        // If we're showing the industry modal, explicitly set to not_listed
        params.set("source_industry", "not_listed")
      } else {
        params.set("source_industry", values.industry)
      }
    }

    if (values.country) {
      if (modalType === "country") {
        // If we're showing the country modal, explicitly set to not_listed
        params.set("source_country", "not_listed")
      } else {
        params.set("source_country", values.country)
      }
    }

    if (values.budget) {
      params.set("source_budget", values.budget)
    }

    if (values.businessEmail) {
      params.set("email", values.businessEmail)
    }

    // Add current URL parameters (like UTM params) to the signup URL
    const currentParams = new URLSearchParams(location.search)
    currentParams.forEach((value, key) => {
      if (!params.has(key)) {
        params.set(key, value)
      }
    })

    return `${baseUrl}${params.toString() ? "?" + params.toString() : ""}`
  }

  // Check if submit button should be disabled
  const isSubmitDisabled = (): boolean => {
    return (
      isSubmitting ||
      emailValidation.isValidating ||
      values.companyName.trim() === "" ||
      values.businessEmail.trim() === "" ||
      phoneNumber.trim() === "" ||
      emailValidation.isValid === false ||
      (values.businessEmail.trim() !== "" &&
        emailValidation.isValid !== true &&
        emailValidation.lastValidatedEmail !==
          values.businessEmail.trim().toLowerCase())
    )
  }

  return (
    <FilterContainer>
      <Title>Enter a few details and unlock a list of top creators</Title>
      <form onSubmit={handleSubmit}>
        {/* Honeypot field - hidden from users but bots might fill it */}
        <input
          type="text"
          name="website"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            opacity: 0,
            pointerEvents: "none",
          }}
          tabIndex={-1}
          autoComplete="off"
        />

        <FiltersGrid>
          <FormGroup>
            <Label htmlFor="industry">What industry is your brand in?</Label>
            <Select
              options={INDUSTRY_OPTIONS}
              value={values.industry}
              onChange={(value) => handleChange("industry", value)}
              placeholder="Select your industry..."
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="country">
              Which country are you running campaigns in?
            </Label>
            <Select
              options={COUNTRY_OPTIONS}
              value={values.country}
              onChange={(value) => handleChange("country", value)}
              placeholder="Select your target market"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="budget">
              What's your monthly budget for influencers?
            </Label>
            <Select
              options={BUDGET_OPTIONS}
              value={values.budget}
              onChange={(value) => handleChange("budget", value)}
              placeholder="Campaign budget range"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              name="companyName"
              type="text"
              value={values.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              placeholder="Your Company"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <IntlTelInput
              ref={phoneInputRef}
              onChangeNumber={(number) => {
                setPhoneNumber(number)
              }}
              initOptions={{
                initialCountry: "auto",
                loadUtils: () => import("intl-tel-input/build/js/utils"),
                geoIpLookup: (callback) => {
                  fetch("https://ipapi.co/json")
                    .then((res) => res.json())
                    .then((data) => callback(data.country_code))
                    .catch(() => callback("hr"))
                },
              }}
              inputProps={{
                id: "phoneNumber",
                className: "intl-tel-input",
                type: "tel",
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="businessEmail">Your Business Email</Label>
            <InputContainer>
              <Input
                data-of-track="email"
                id="businessEmail"
                type="email"
                value={values.businessEmail}
                onChange={(e) => handleChange("businessEmail", e.target.value)}
                placeholder="you@company.com"
                required
              />
              {emailValidation.isValidating && <ValidationSpinner />}
            </InputContainer>
            {errors.businessEmail && (
              <ValidationMessage type="error">
                {errors.businessEmail}
              </ValidationMessage>
            )}
            {emailValidation.isValidating && (
              <ValidationMessage type="info">
                Verifying email address...
              </ValidationMessage>
            )}
            {emailValidation.isValid === true && !errors.businessEmail && (
              <ValidationMessage type="success">
                ✓ Email verified
              </ValidationMessage>
            )}
          </FormGroup>
        </FiltersGrid>

        <ButtonContainer>
          <Button
            data-of-track="submit"
            type="submit"
            disabled={isLoading || isSubmitDisabled()}
            fullWidth
            size="large"
          >
            {isSubmitting ? "⏳ Processing..." : "🔓 Unlock My List →"}
          </Button>
        </ButtonContainer>
      </form>

      {/* Custom Industry Modal */}
      <Modal isVisible={showNotListedModal}>
        <ModalContent>
          <CloseButton onClick={closeModal}>×</CloseButton>

          <ModalHeader>
            <ModalBadge>🎉 VIP Access Unlocked</ModalBadge>
            {modalType === "industry" ? (
              <ModalTitle>
                Why settle for a generic industry list…when we can hand-pick
                creators <em>based on your exact product?</em>
              </ModalTitle>
            ) : (
              <ModalTitle>
                Why settle for a generic list…when we can hand-pick creators{" "}
                <em>based on your exact needs, without limitations?</em>
              </ModalTitle>
            )}
          </ModalHeader>

          <ModalText>
            No problem, we'll give you the VIP treatment you actually deserve.
            Sign up now and generate your personalized influencer list in
            minutes - 100% free.
          </ModalText>

          <ModalActions>
            <Button
              onClick={() => {
                window.location.href = getCustomSignupUrl()
              }}
            >
              Sign Up For Free
            </Button>
            <OutlinedButton
              onClick={() => {
                // Reset to beauty category and close modal
                handleChange(
                  modalType,
                  modalType === "industry" ? "beauty-cosmetics" : "uk"
                )
                setShowNotListedModal(false)
              }}
            >
              Go Back
            </OutlinedButton>
          </ModalActions>
        </ModalContent>
      </Modal>
    </FilterContainer>
  )
}

export default FilterSection
